import React, { useMemo } from "react";
import { ESTATE_CATEGORIES, DEAL_TYPES, USE_AREA_GROUPS, FLOOR_OPTIONS,
  APPROVAL_OPTIONS, AVAILABLE_DATE_OPTIONS, ROOM_COUNT_OPTIONS,
  BATHROOM_COUNT_OPTIONS, DIRECTION_OPTIONS, formatPrice, M2_TO_PYEONG } from "../constants/filterOptions";
import HorizontalScrollContainer from "./HorizontalScrollContainer";
import styles from "./FilterChipBar.module.scss";

/**
 * 수평 스크롤 필터 칩 바
 *
 * 피그마: 상단바 하단 영역 — "전체" 버튼 + 리셋 + 카테고리칩(선택시 강조) + 개별 필터 칩들
 * 선택된 부동산 유형에 따라 칩 구성이 달라짐
 * 활성 필터는 칩 텍스트에 선택 내용 요약을 표시 (레거시 동일 방식)
 */

// 카테고리별 추가 칩 매핑
const CATEGORY_EXTRA_CHIPS = {
  "10,11,17,18,19,15": ["approval", "useArea", "floor"],
  "0,1,2": ["approval", "useArea", "floor", "availableDate", "interior", "direction"],
  "7,8,3,4,5,6": ["approval", "useArea", "floor", "availableDate", "interior", "direction"],
  "16": ["useArea"],
};

const EXTRA_CHIP_DEFS = {
  approval: { id: "approval", label: "사용승인일", keys: ["approvalYear"] },
  useArea: { id: "useArea", label: "용도지역", keys: ["useArea"] },
  floor: { id: "floor", label: "층수", keys: ["floorType"] },
  availableDate: { id: "availableDate", label: "입주가능일", keys: ["availableDate"] },
  interior: { id: "interior", label: "내부구조", keys: ["roomCnt", "bathroomCnt"] },
  direction: { id: "direction", label: "방향", keys: ["direction"] },
};

function buildChips(selectedCategory) {
  const selectedCat = ESTATE_CATEGORIES.find((c) => c.value === selectedCategory);

  const categoryChip = {
    id: "estateType",
    label: selectedCat ? selectedCat.label : "전체 매물유형",
    keys: ["randType"],
    isCategory: true,
    isCategoryActive: !!selectedCat,
  };

  const baseChips = [
    { id: "dealType", label: "거래유형", keys: ["deal_type"] },
    { id: "price", label: "가격", keys: ["minPrice", "maxPrice", "minDeposit", "maxDeposit", "minMonthly", "maxMonthly"] },
    { id: "area", label: selectedCategory === "16" ? "토지면적" : "면적", keys: ["minArea", "maxArea"] },
  ];

  const extras = selectedCategory
    ? (CATEGORY_EXTRA_CHIPS[selectedCategory] || ["useArea"])
    : ["useArea"];

  const extraChips = extras.map((key) => EXTRA_CHIP_DEFS[key]).filter(Boolean);

  return [categoryChip, ...baseChips, ...extraChips];
}

// ── 요약 라벨 헬퍼 ──

// 다중 선택: "A" / "A, B" / "A, B +N"
function multiLabel(selectedVals, allOptions, defaultLabel) {
  if (!selectedVals || selectedVals.length === 0) return defaultLabel;
  const selected = allOptions.filter((o) => selectedVals.includes(o.value));
  if (selected.length === 0) return defaultLabel;
  if (selected.length === 1) return selected[0].label;
  if (selected.length === 2) return `${selected[0].label}, ${selected[1].label}`;
  return `${selected[0].label}, ${selected[1].label} +${selected.length - 2}`;
}

// 가격 범위: "매 1억~2억 ・ 보 5000만~"
function priceLabel(payload) {
  const { minPrice, maxPrice, minDeposit, maxDeposit, minMonthly, maxMonthly, deal_type } = payload;
  const dealTypes = deal_type || [];
  const isAll = dealTypes.length === 0;
  // 숫자/문자열 혼용 대응
  const has = (v) => dealTypes.includes(v) || dealTypes.includes(String(v));
  const showSale    = isAll || has(0);
  const showDeposit = isAll || has(1) || has(2);
  const showMonthly = isAll || has(2);

  const fmtRange = (prefix, min, max) => {
    const hasMin = min != null && Number(min) !== 0;
    const hasMax = max != null;
    if (!hasMin && !hasMax) return "";
    if (hasMin && hasMax)   return `${prefix} ${formatPrice(min)}~${formatPrice(max)}`;
    if (hasMin)             return `${prefix} ${formatPrice(min)}~`;
    return                         `${prefix} ~${formatPrice(max)}`;
  };

  const parts = [
    showSale    ? fmtRange("매", minPrice, maxPrice)     : "",
    showDeposit ? fmtRange("보", minDeposit, maxDeposit) : "",
    showMonthly ? fmtRange("월", minMonthly, maxMonthly) : "",
  ].filter(Boolean);

  return parts.join(" ・ ") || "가격";
}

// 면적 범위: "30~50평" / "99~165m²"
function areaLabel(payload, isLand) {
  const { minArea, maxArea, areaUnit } = payload;
  const isPyeong = areaUnit !== "sqm";
  const unit = isPyeong ? "평" : "m²";
  const conv = (v) => isPyeong ? Math.round(v * M2_TO_PYEONG) : Math.round(v);
  const defaultLabel = isLand ? "토지면적" : "면적";

  const hasMin = minArea != null && Number(minArea) !== 0;
  const hasMax = maxArea != null;
  if (!hasMin && !hasMax) return defaultLabel;
  if (hasMin && hasMax)   return `${conv(minArea)}~${conv(maxArea)}${unit}`;
  if (hasMin)             return `${conv(minArea)}${unit}~`;
  return                         `~${conv(maxArea)}${unit}`;
}

// 내부구조: "방수 1개, 2개 ・ 욕실수 1개"
function interiorLabel(payload) {
  const roomCnt    = payload.roomCnt    || [];
  const bathroomCnt = payload.bathroomCnt || [];
  if (!roomCnt.length && !bathroomCnt.length) return "내부구조";

  const fmtGroup = (prefix, vals, opts) => {
    const labels = opts.filter((o) => vals.includes(o.value)).map((o) => o.label);
    if (!labels.length) return "";
    if (labels.length === 1) return `${prefix} ${labels[0]}`;
    if (labels.length === 2) return `${prefix} ${labels[0]}, ${labels[1]}`;
    return `${prefix} ${labels[0]}, ${labels[1]} +${labels.length - 2}`;
  };

  return [
    fmtGroup("방수", roomCnt, ROOM_COUNT_OPTIONS),
    fmtGroup("욕실수", bathroomCnt, BATHROOM_COUNT_OPTIONS),
  ].filter(Boolean).join(" ・ ") || "내부구조";
}

// 칩별 표시 라벨 결정
function getChipLabel(chip, payload) {
  const isLand = payload.selectedCategory === "16";

  switch (chip.id) {
    case "estateType": {
      const selectedCat = ESTATE_CATEGORIES.find((c) => c.value === payload.selectedCategory);
      if (!selectedCat) return "전체 매물유형";

      // randType에서 실제 선택된 값 확인
      const randType = payload.randType;
      const valueArray = (() => {
        if (!randType || (Array.isArray(randType) && randType.length === 0)) return [];
        if (typeof randType === "string") return randType.split(",").map((s) => s.trim()).filter(Boolean);
        return Array.isArray(randType) ? randType.map(String) : [];
      })();

      // 카테고리 내 전체 서브옵션이 선택된 경우 → 카테고리명
      const allSubValues = selectedCat.subOptions.flatMap((o) =>
        String(o.value).split(",").map((s) => s.trim())
      );
      const allSelected = allSubValues.every((v) => valueArray.includes(v));
      if (allSelected || valueArray.length === 0) return selectedCat.label;

      // 개별 서브옵션만 선택된 경우 → 서브옵션 라벨 표시
      const selectedLabels = selectedCat.subOptions
        .filter((opt) => {
          const parts = String(opt.value).split(",").map((s) => s.trim());
          return parts.length > 0 && parts.every((p) => valueArray.includes(p));
        })
        .map((opt) => opt.label);

      if (selectedLabels.length === 0) return selectedCat.label;
      if (selectedLabels.length === 1) return selectedLabels[0];
      if (selectedLabels.length === 2) return `${selectedLabels[0]}, ${selectedLabels[1]}`;
      return `${selectedLabels[0]}, ${selectedLabels[1]} +${selectedLabels.length - 2}`;
    }

    case "dealType":
      return multiLabel(payload.deal_type, DEAL_TYPES, chip.label);

    case "price":
      return priceLabel(payload);

    case "area":
      return areaLabel(payload, isLand);

    case "useArea": {
      const selected = payload.useArea || [];
      if (selected.length === 0) return chip.label;

      // 그룹 전체 선택이면 그룹 라벨, 개별 선택이면 항목 라벨로 분해
      const parts = [];
      for (const group of USE_AREA_GROUPS) {
        const groupVals = group.options.map((o) => String(o.value));
        const selectedInGroup = selected.filter((v) => groupVals.includes(String(v)));
        if (selectedInGroup.length === 0) continue;
        if (selectedInGroup.length === group.options.length) {
          parts.push(group.label);
        } else {
          const labels = group.options
            .filter((o) => selectedInGroup.some((v) => String(v) === String(o.value)))
            .map((o) => o.label);
          parts.push(...labels);
        }
      }

      if (parts.length === 0) return chip.label;
      if (parts.length === 1) return parts[0];
      if (parts.length === 2) return `${parts[0]}, ${parts[1]}`;
      return `${parts[0]}, ${parts[1]} +${parts.length - 2}`;
    }

    case "floor":
      return multiLabel(payload.floorType, FLOOR_OPTIONS, chip.label);

    case "approval": {
      const opt = APPROVAL_OPTIONS.find((o) => o.value === payload.approvalYear);
      return opt ? opt.label : chip.label;
    }

    case "availableDate":
      return multiLabel(payload.availableDate, AVAILABLE_DATE_OPTIONS, chip.label);

    case "interior":
      return interiorLabel(payload);

    case "direction":
      return multiLabel(payload.direction, DIRECTION_OPTIONS, chip.label);

    default:
      return chip.label;
  }
}

const FilterChipBar = ({ payload, onChange, onFullFilter, onChipClick, onReset }) => {
  const selectedCategory = payload.selectedCategory || null;

  const chips = useMemo(
    () => buildChips(selectedCategory),
    [selectedCategory]
  );

  const hasActiveFilter = (key) => {
    const v = payload[key];
    if (v == null || v === "") return false;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  };

  // 가격 칩은 현재 deal_type에 해당하는 키만 활성 판단
  const hasActivePriceFilter = () => {
    const dealTypes = payload.deal_type || [];
    const isAll = dealTypes.length === 0;
    const has = (v) => dealTypes.includes(v) || dealTypes.includes(String(v));
    const showSale    = isAll || has(0);
    const showDeposit = isAll || has(1) || has(2);
    const showMonthly = isAll || has(2);

    if (showSale    && (hasActiveFilter("minPrice")   || hasActiveFilter("maxPrice")))   return true;
    if (showDeposit && (hasActiveFilter("minDeposit")  || hasActiveFilter("maxDeposit"))) return true;
    if (showMonthly && (hasActiveFilter("minMonthly")  || hasActiveFilter("maxMonthly"))) return true;
    return false;
  };

  // 초기화 버튼: 하나라도 필터가 활성이면 enable
  const hasAnyFilter = useMemo(() => {
    const checkKeys = [
      "deal_type", "randType", "useArea", "floorType", "availableDate",
      "roomCnt", "bathroomCnt", "direction", "approvalYear",
      "minPrice", "maxPrice", "minDeposit", "maxDeposit", "minMonthly", "maxMonthly",
      "minArea", "maxArea", "ownerVerifiedOnly", "selectedCategory",
    ];
    return checkKeys.some((k) => hasActiveFilter(k));
  }, [payload]);

  return (
    <HorizontalScrollContainer className={styles.container}>
      {/* 전체 필터 버튼 */}
      <button
        type="button"
        className={`${styles.chip} ${styles.fullFilterChip}`}
        onClick={onFullFilter}
      >
        <FilterIcon />
        <span>전체</span>
        <CaretDownIcon />
      </button>

      {/* 리셋 버튼 */}
      <button
        type="button"
        className={`${styles.resetChip} ${!hasAnyFilter ? styles.resetDisabled : ""}`}
        onClick={hasAnyFilter ? onReset : undefined}
        disabled={!hasAnyFilter}
      >
        <ResetSmallIcon />
      </button>

      {/* 소유자 인증 매물 토글 — 모달 없이 직접 토글 */}
      <button
        type="button"
        className={`${styles.chip} ${styles.toggleChip} ${payload.ownerVerifiedOnly ? styles.toggleActive : ""}`}
        onClick={() => onChange("ownerVerifiedOnly", payload.ownerVerifiedOnly ? "" : "true")}
      >
        <span>소유자 인증 매물만 보기</span>
        <span className={styles.toggleSwitch} data-checked={!!payload.ownerVerifiedOnly} />
      </button>

      {/* 동적 필터 칩 */}
      {chips.map((chip) => {
        const isActive = chip.isCategoryActive || (chip.id === "price" ? hasActivePriceFilter() : chip.keys.some((k) => hasActiveFilter(k)));
        const label = getChipLabel(chip, payload);
        return (
          <button
            key={chip.id}
            type="button"
            className={`${styles.chip} ${isActive ? styles.active : ""}`}
            onClick={(e) => onChipClick(chip.id, e)}
          >
            <span>{label}</span>
            <CaretDownIcon />
          </button>
        );
      })}
    </HorizontalScrollContainer>
  );
};

// ── 아이콘 ──

const FilterIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g transform="translate(3,3)">
      <path d="M8.4205 18C8.20683 18 8.02917 17.9281 7.8875 17.7843C7.74583 17.6406 7.675 17.4625 7.675 17.25V13.125C7.675 12.9125 7.74733 12.7343 7.892 12.5905C8.0365 12.4468 8.21567 12.375 8.4295 12.375C8.64317 12.375 8.82083 12.4468 8.9625 12.5905C9.10417 12.7343 9.175 12.9125 9.175 13.125V14.45H17.25C17.4625 14.45 17.6407 14.5223 17.7845 14.667C17.9282 14.8115 18 14.9907 18 15.2045C18 15.4182 17.9282 15.5958 17.7845 15.7375C17.6407 15.8792 17.4625 15.95 17.25 15.95H9.175V17.25C9.175 17.4625 9.10267 17.6406 8.958 17.7843C8.8135 17.9281 8.63433 18 8.4205 18ZM0.75 15.95C0.5375 15.95 0.359417 15.8777 0.21575 15.733C0.0719168 15.5885 0 15.4093 0 15.1955C0 14.9818 0.0719168 14.8042 0.21575 14.6625C0.359417 14.5208 0.5375 14.45 0.75 14.45H5.425C5.6375 14.45 5.81567 14.5223 5.9595 14.667C6.10317 14.8115 6.175 14.9907 6.175 15.2045C6.175 15.4182 6.10317 15.5958 5.9595 15.7375C5.81567 15.8792 5.6375 15.95 5.425 15.95H0.75ZM5.4205 11.8C5.20683 11.8 5.02917 11.7281 4.8875 11.5843C4.74583 11.4406 4.675 11.2625 4.675 11.05V9.75H0.75C0.5375 9.75 0.359417 9.67767 0.21575 9.533C0.0719168 9.3885 0 9.20933 0 8.9955C0 8.78183 0.0719168 8.60417 0.21575 8.4625C0.359417 8.32083 0.5375 8.25 0.75 8.25H4.675V6.9C4.675 6.6875 4.74733 6.50933 4.892 6.3655C5.0365 6.22183 5.21567 6.15 5.4295 6.15C5.64317 6.15 5.82083 6.22183 5.9625 6.3655C6.10417 6.50933 6.175 6.6875 6.175 6.9V11.05C6.175 11.2625 6.10267 11.4406 5.958 11.5843C5.8135 11.7281 5.63433 11.8 5.4205 11.8ZM8.425 9.75C8.2125 9.75 8.03442 9.67767 7.89075 9.533C7.74692 9.3885 7.675 9.20933 7.675 8.9955C7.675 8.78183 7.74692 8.60417 7.89075 8.4625C8.03442 8.32083 8.2125 8.25 8.425 8.25H17.25C17.4625 8.25 17.6407 8.32233 17.7845 8.467C17.9282 8.6115 18 8.79067 18 9.0045C18 9.21817 17.9282 9.39583 17.7845 9.5375C17.6407 9.67917 17.4625 9.75 17.25 9.75H8.425ZM12.5705 5.625C12.3568 5.625 12.1792 5.55308 12.0375 5.40925C11.8958 5.26558 11.825 5.0875 11.825 4.875V0.75C11.825 0.5375 11.8973 0.359333 12.042 0.2155C12.1865 0.0718332 12.3657 0 12.5795 0C12.7932 0 12.9708 0.0718332 13.1125 0.2155C13.2542 0.359333 13.325 0.5375 13.325 0.75V2.05H17.25C17.4625 2.05 17.6407 2.12233 17.7845 2.267C17.9282 2.4115 18 2.59067 18 2.8045C18 3.01817 17.9282 3.19583 17.7845 3.3375C17.6407 3.47917 17.4625 3.55 17.25 3.55H13.325V4.875C13.325 5.0875 13.2527 5.26558 13.108 5.40925C12.9635 5.55308 12.7843 5.625 12.5705 5.625ZM0.75 3.55C0.5375 3.55 0.359417 3.47767 0.21575 3.333C0.0719168 3.1885 0 3.00933 0 2.7955C0 2.58183 0.0719168 2.40417 0.21575 2.2625C0.359417 2.12083 0.5375 2.05 0.75 2.05H9.575C9.7875 2.05 9.96567 2.12233 10.1095 2.267C10.2532 2.4115 10.325 2.59067 10.325 2.8045C10.325 3.01817 10.2532 3.19583 10.1095 3.3375C9.96567 3.47917 9.7875 3.55 9.575 3.55H0.75Z" fill="currentColor"/>
    </g>
  </svg>
);

const CaretDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ResetSmallIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.87972 5.64276L6.78103 4.54407C9.20986 3.1078 12.3926 3.43423 14.4796 5.52127C16.9529 7.99459 16.9529 12.0046 14.4796 14.478C12.0063 16.9513 7.99623 16.9513 5.52291 14.478C4.28592 13.241 3.66773 11.6211 3.66792 9.99888C3.66797 9.58467 3.33222 9.24884 2.918 9.24879C2.50379 9.24875 2.16797 9.5845 2.16792 9.99871C2.16769 12.0026 2.93261 14.009 4.46225 15.5386C7.52136 18.5977 12.4811 18.5977 15.5403 15.5386C18.5994 12.4795 18.5994 7.51971 15.5403 4.46061C12.8636 1.78397 8.73342 1.44982 5.69282 3.45586L4.63881 2.40185C4.42431 2.18735 4.10173 2.12319 3.82147 2.23927C3.54122 2.35536 3.35848 2.62884 3.35848 2.93218L3.35848 6.17309C3.35848 6.372 3.4375 6.56277 3.57815 6.70342C3.71881 6.84407 3.90957 6.92309 4.10848 6.92309H7.34939C7.65274 6.92309 7.92621 6.74036 8.0423 6.4601C8.15838 6.17985 8.09422 5.85726 7.87972 5.64276Z" fill="currentColor" />
  </svg>
);

export default FilterChipBar;
